// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/lib/trains.ts
// 🔧 PURPOSE: Tabellone partenze della stazione di Mola di Bari, dall'endpoint
//   pubblico del channel manager (nessuna autenticazione: qualunque ospite lo
//   vede, anche prima del check-in). L'ultimo tabellone resta in cache in
//   memoria per 60s: riaprire TrainsApp subito dopo non genera una nuova
//   richiesta di rete.
// ============================================

// Stessa base URL di CHANNEL_MANAGER_URL in src/lib/guestApi.ts. Duplicata
// qui (non esportata da guestApi.ts) per non dover toccare quel file.
const CHANNEL_MANAGER_URL = 'https://book.nonnavittoriaapartments.it';

export type TrainDirection = 'north' | 'south' | 'other';

export interface TrainDeparture {
  /** Orario locale "HH:MM" come mostrato in stazione. */
  time: string;
  /** Istante della partenza in epoch ms, quando noto (serve per ordinare e per il countdown). */
  epochMs: number | null;
  destination: string;
  /** Categoria del treno, es. "REG", "IC". */
  category: string;
  trainNumber: string;
  platform: string | null;
  delayMin: number | null;
  direction: TrainDirection;
}

export interface TrainBoard {
  stationName: string;
  fetchedAt: string | null;
  departures: TrainDeparture[];
}

const CACHE_TTL_MS = 60_000;
let cachedBoard: TrainBoard | null = null;
let cachedAt = 0;

function isValidDirection(value: unknown): value is TrainDirection {
  return value === 'north' || value === 'south' || value === 'other';
}

function parseBoard(data: unknown): TrainBoard | null {
  const payload = data as { success?: unknown; board?: unknown } | null | undefined;
  if (!payload || payload.success !== true) return null;

  const board = payload.board as
    | { stationName?: unknown; fetchedAt?: unknown; departures?: unknown }
    | null
    | undefined;
  if (!board || !Array.isArray(board.departures)) return null;

  const departures: TrainDeparture[] = board.departures.map((raw): TrainDeparture => {
    const d = (raw || {}) as Record<string, unknown>;
    return {
      time: typeof d.time === 'string' ? d.time : '',
      epochMs: typeof d.epochMs === 'number' ? d.epochMs : null,
      destination: typeof d.destination === 'string' ? d.destination : '',
      category: typeof d.category === 'string' ? d.category : '',
      trainNumber: typeof d.trainNumber === 'string' ? d.trainNumber : '',
      platform: typeof d.platform === 'string' ? d.platform : null,
      delayMin: typeof d.delayMin === 'number' ? d.delayMin : null,
      direction: isValidDirection(d.direction) ? d.direction : 'other',
    };
  });

  return {
    stationName: typeof board.stationName === 'string' ? board.stationName : '',
    fetchedAt: typeof board.fetchedAt === 'string' ? board.fetchedAt : null,
    departures,
  };
}

/**
 * Prende il tabellone partenze di Mola di Bari. Endpoint pubblico, nessun
 * token. Risposta non-ok o errore di rete → null (mai un errore da
 * propagare: TrainsApp mostra semplicemente "orari non disponibili").
 *
 * `forceRefresh` salta la cache di 60s (usato dal pulsante di refresh in
 * TrainsApp); di norma non serve passarlo.
 */
export const fetchTrainBoard = async (forceRefresh = false): Promise<TrainBoard | null> => {
  const now = Date.now();
  if (!forceRefresh && cachedBoard && now - cachedAt < CACHE_TTL_MS) {
    return cachedBoard;
  }

  try {
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/trains`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) return null;

    const data = await response.json();
    const board = parseBoard(data);
    if (board) {
      cachedBoard = board;
      cachedAt = now;
    }
    return board;
  } catch (error) {
    console.error('Fetch train board error:', error);
    return null;
  }
};

/**
 * "14:35" di oggi in Italia → epoch ms (null se l'orario non è valido).
 * Serve quando ViaggiaTreno non manda `epochMs`: senza questo il treno
 * sparirebbe dall'elenco invece di restare in coda con il suo orario.
 */
export const epochFromRomeTime = (hhmm: string): number | null => {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm || '');
  if (!m) return null;
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: 'Europe/Rome', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(new Date());
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value || 0);
  // Offset Roma↔UTC adesso: differenza fra l'ora di Roma e l'ora UTC dello stesso istante.
  const nowUtc = Date.now();
  const romeAsUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'));
  const offsetMs = romeAsUtc - Math.floor(nowUtc / 60_000) * 60_000;
  return Date.UTC(get('year'), get('month') - 1, get('day'), Number(m[1]), Number(m[2])) - offsetMs;
};

/**
 * Le partenze ancora da fare, in ordine di orario. `graceMs` è il margine di
 * tolleranza all'indietro (il tabellone in stazione tiene il treno qualche
 * minuto dopo l'orario); Esplora lo vuole a zero, TrainsApp a due minuti.
 */
export const upcomingDepartures = (
  board: TrainBoard | null,
  now: number,
  graceMs = 0,
): TrainDeparture[] => {
  if (!board) return [];
  const cutoff = now - graceMs;
  return board.departures
    .map((d) => (d.epochMs !== null ? d : { ...d, epochMs: epochFromRomeTime(d.time) }))
    .filter((d) => d.epochMs !== null && (d.epochMs as number) >= cutoff)
    .sort((a, b) => (a.epochMs as number) - (b.epochMs as number));
};

/**
 * Il conto alla rovescia di una partenza, con le chiavi `trains.countdown.*`:
 * entro 1 minuto "ora", poi minuti, oltre l'ora "1h 20m". Lo usano il
 * tabellone (TrainsApp, ricalcolato ogni 30s) e la testata di Esplora.
 */
export function formatTrainCountdown(
  epochMs: number,
  now: number,
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  const diffMs = epochMs - now;
  if (diffMs <= 60_000) return t('trains.countdown.now');
  const diffMin = Math.round(diffMs / 60_000);
  if (diffMin < 60) return t('trains.countdown.minutes', { count: diffMin });
  const hours = Math.floor(diffMin / 60);
  const minutes = diffMin % 60;
  return t('trains.countdown.hoursMinutes', { hours, minutes });
}

/** "Bari Centrale" → "Bari": nei riquadri di Esplora ci sta una parola sola. */
export function shortStationName(destination: string): string {
  const first = String(destination || '').trim().split(/[\s/,]+/)[0];
  return first || String(destination || '');
}
